import { format, isValid } from "date-fns";
import { DateRange } from "..";
import { DataGridSortableColumns, ExportDeclarationsQuery, SortDirection } from "../../graphql/types";
import { IDateRange } from "../../types";
import { ExportFilter, SourceNode, toTreeExportNodes } from "../common/FilterPublishedArtifacts";
import { addChildToNode } from "../Filters/Common";

const getDateRange = (start?: string, finish?: string) => {
  if (!start || !finish) return undefined;
  return DateRange.fromString(start, finish)?.toDateRange();
};

interface IUploadsFilter {
  projects: string[];
  exportNames: string[];
  search: string;
  status: string;
  reportRange: IDateRange;
  purpose: string;
  uploadedRange: IDateRange;
  destination?: string;
  sortBy?: DataGridSortableColumns;
  sortDirection?: SortDirection;
}

const getUploadsDatagridQueryVariables = (filter: IUploadsFilter) => {
  const today = format(new Date(), "yyyy-MM-dd");

  const reportEnd =
    filter.reportRange.end && isValid(filter.reportRange.end) ? format(filter.reportRange.end, "yyyy-MM-dd") : null;
  const reportDatetimeBefore = reportEnd && reportEnd !== today ? reportEnd : null;
  const reportDatetimeAfter =
    filter.reportRange.start && isValid(filter.reportRange.start)
      ? format(filter.reportRange.start, "yyyy-MM-dd")
      : null;
  const uploadedEnd =
    filter.uploadedRange.end && isValid(filter.uploadedRange.end)
      ? format(filter.uploadedRange.end, "yyyy-MM-dd")
      : null;
  const createdDatetimeBefore = uploadedEnd && uploadedEnd !== today ? uploadedEnd : null;
  const createdDatetimeAfter =
    filter.uploadedRange.start && isValid(filter.uploadedRange.start)
      ? format(filter.uploadedRange.start, "yyyy-MM-dd")
      : null;

  return {
    first: 20,
    projects: filter.projects.length ? filter.projects : null,
    names: filter.exportNames.length ? filter.exportNames : null,
    purpose: filter.purpose,
    search: filter.search,
    status: filter.status,
    report_datetime_after: reportDatetimeAfter,
    report_datetime_before: reportDatetimeBefore,
    uploaded_before: createdDatetimeBefore,
    uploaded_after: createdDatetimeAfter,
    destination: filter.destination ?? null,
    sortBy: filter.sortBy,
    sortDirection: filter.sortDirection,
  };
};

type UploadFiltersParamNames = { [K in keyof Omit<IUploadsFilter, "reportRange" | "uploadedRange">]-?: string } & {
  reportRange: { start: string; end: string };
  uploadedRange: { start: string; end: string };
};

const getDefaultUploadFilters = (
  searchParams: URLSearchParams,
  useDefaultDateRange?: boolean,
  variables?: Partial<IUploadsFilter>,
  paramNames?: UploadFiltersParamNames
): IUploadsFilter => {
  return {
    projects: variables?.projects || searchParams.get(paramNames?.projects || "project")?.split(",") || [],
    exportNames: variables?.exportNames || searchParams.get(paramNames?.exportNames || "uploadName")?.split(",") || [],
    search: variables?.search || searchParams.get(paramNames?.search || "search") || "",
    status: variables?.status || searchParams.get(paramNames?.status || "status") || "",
    reportRange: getDateRange(
      (variables?.reportRange?.start && format(variables?.reportRange?.start, "yyyy-MM-dd")) ||
        searchParams.get(paramNames?.reportRange?.start || "start") ||
        undefined,
      (variables?.reportRange?.end && format(variables?.reportRange?.end, "yyyy-MM-dd")) ||
        searchParams.get(paramNames?.reportRange?.end || "end") ||
        undefined
    ) || {
      start: null,
      end: null,
    },
    purpose: variables?.purpose || searchParams.get(paramNames?.purpose || "purpose") || "",
    uploadedRange:
      getDateRange(
        (variables?.uploadedRange?.start && format(variables?.uploadedRange?.start, "yyyy-MM-dd")) ||
          searchParams.get(paramNames?.uploadedRange?.start || "uploaded_start") ||
          undefined,
        (variables?.uploadedRange?.end && format(variables?.uploadedRange?.end, "yyyy-MM-dd")) ||
          searchParams.get(paramNames?.uploadedRange?.end || "uploaded_end") ||
          undefined
      ) ||
      (useDefaultDateRange
        ? DateRange.fromDays(7).toDateRange()
        : {
            start: null,
            end: null,
          }),

    destination: variables?.destination || searchParams.get(paramNames?.destination || "destination") || undefined,
    sortBy:
      variables?.sortBy || (searchParams.get(paramNames?.sortBy || "sortBy") as DataGridSortableColumns) || undefined,
    sortDirection:
      variables?.sortDirection ||
      (searchParams.get(paramNames?.sortDirection || "sortDirection") as SortDirection) ||
      undefined,
  };
};

const matchesFilter = (filterValue: ExportFilter, filter: IUploadsFilter) =>
  (filterValue.project && filter.projects?.includes(filterValue.project)) ||
  (filterValue.exportName && filter.exportNames?.includes(filterValue.exportName));

const getUploadsNodes = (data: ExportDeclarationsQuery | undefined, filter: IUploadsFilter) => {
  let nodes: SourceNode[] = [];
  const nonSelectedNodes: SourceNode[] = [];
  const selectedNodes: SourceNode[] = [];

  if (data) {
    const exports = data.export_declarations?.edges.map((edge) => edge.node);

    if (exports) {
      nodes = toTreeExportNodes(exports, true, true);
    }
  }

  nodes.forEach((node: SourceNode) => {
    const filterValue = node.filterValue as ExportFilter;
    const isMatching = matchesFilter(filterValue, filter);

    if (node.children.length === 0) {
      // If the node has no children, we can add it directly to the selected or non-selected nodes
      // Otherwise it will be added to the selected or non-selected nodes when we process its children
      (isMatching ? selectedNodes : nonSelectedNodes).push(node);
    }

    node.children.forEach((child) => {
      const childFilterValue = child.filterValue as ExportFilter;
      if (matchesFilter(childFilterValue, filter)) {
        addChildToNode(selectedNodes, child, node);
      } else {
        addChildToNode(nonSelectedNodes, child, node);
      }
    });
  });
  return { selectedNodes, nonSelectedNodes };
};

export {
  getDefaultUploadFilters,
  getUploadsDatagridQueryVariables,
  getUploadsNodes,
  IUploadsFilter,
  UploadFiltersParamNames,
};
